import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { domainQueryKeys, Domains } from '#api';
import type { Domain } from '#dn-types/domain.ts';
import { makeMaxResourceQueryFn } from '#util/api.ts';

/**
 * Fetches all domains, paginating until we run out of domains
 * (We don't expect more than one page, really)
 */
export const maxDomainsQueryOptions = queryOptions({
  queryKey: domainQueryKeys.maxList,
  queryFn: makeMaxResourceQueryFn(Domains.getDomains),
});

export function useSuspenseMaxDomainsQuery() {
  return useSuspenseQuery(maxDomainsQueryOptions);
}

export const makeDomainQueryOptions = (id: string) =>
  queryOptions({
    queryKey: domainQueryKeys.detail(id),
    queryFn: ({ signal }) => Domains.getDomain(id, { signal }),
  });

/**
 * For providers which may or may not have a domainID.
 */
export function useConditionalDomainQuery(domainId: string | null, { enabled }: { enabled?: boolean } = {}) {
  return useQuery({
    ...makeDomainQueryOptions(domainId ?? ''),
    // If domainId is falsey and enabled isn't provided, disable the query
    enabled: enabled ?? Boolean(domainId),
    throwOnError: true,
  });
}

export function useSuspenseDomainQuery(id: string) {
  return useSuspenseQuery(makeDomainQueryOptions(id));
}

export function useAddDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: Domains.addDomain,
    async onSuccess(domain) {
      queryClient.setQueryData(makeDomainQueryOptions(domain.id).queryKey, { data: domain });
      await queryClient.invalidateQueries({ queryKey: domainQueryKeys.maxList });
    },
  });
}

export function useVerifyDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: Domains.verifyDomain,
    async onSuccess(_, domainId) {
      // Update cache for domain query to mark it as verified
      queryClient.setQueryData(makeDomainQueryOptions(domainId).queryKey, (oldData) =>
        oldData ? { ...oldData, data: { ...oldData.data, isVerified: true } } : undefined
      );
      await queryClient.invalidateQueries({ queryKey: domainQueryKeys.maxList });
    },
  });
}

export function useUpdateDomainCache() {
  const queryClient = useQueryClient();

  return function updateDomainCache(domain: Domain) {
    void queryClient.invalidateQueries({ queryKey: domainQueryKeys.lists() });
    // Set the returned domain into our query cache
    queryClient.setQueryData(makeDomainQueryOptions(domain.id).queryKey, { data: domain });
  };
}
